import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.Timestamp
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable
import models.BankoGameEngine
import models.DomainGame
import models.FirebaseGame
import models.GameDataSource
import models.GameSettings
import models.GinkoEvent
import models.JoinGameResult
import models.Nudge
import models.Player
import models.PublicGameSummary
import models.PUBLIC_GAME_MAX_PLAYERS
import models.isPublicListingFresh
import models.Round
import models.RoundModifier
import models.Setting
import models.TableReaction
import models.TableReactionKind
import models.GameRecapStats
import models.afterBank
import models.afterRoll
import models.heistBonusesFor

class RemoteGameDataSource : GameDataSource {
    private val db = Firebase.firestore

    // Absolute time a touched game/player should be eligible for TTL deletion: now + the window.
    // Firestore's TTL policy only reads this field — it has no notion of "inactive for 6h" on its
    // own — so we stamp it here and refresh it on each game mutation.
    private fun gameExpiry(now: Timestamp = Timestamp.now()): Timestamp {
        return Timestamp(now.seconds + GAME_TTL_SECONDS, now.nanoseconds)
    }

    override suspend fun deleteGame(gameCode: String) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val players = gameDocRef.collection("players").get()
        val reactions = gameDocRef.collection("reactions").get()
        val batch = db.batch()
        players.documents.forEach {
            batch.delete(gameDocRef.collection("players").document(it.id))
        }
        reactions.documents.forEach {
            batch.delete(gameDocRef.collection("reactions").document(it.id))
        }
        batch.delete(gameDocRef)
        batch.commit()
    }

    override suspend fun finishGame(gameCode: String) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val now = Timestamp.now()
        gameDocRef.update(
            Pair("finished", true),
            Pair("isPublic", false),
            Pair("publicSince", null),
            Pair("dateLastModified", now),
            Pair("expiresAt", gameExpiry(now)),
        )
    }

    override suspend fun updateSetting(gameCode: String, game: DomainGame, settingToUpdate: Setting) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val currentSettings = game.settings.toMutableList()
        val indexToUpdate = currentSettings.indexOfFirst { it.name == settingToUpdate.name }
        if (indexToUpdate != -1) {
            currentSettings[indexToUpdate] = settingToUpdate
        } else {
            currentSettings.add(settingToUpdate)
        }
        val now = Timestamp.now()
        val updates = mutableListOf<Pair<String, Any?>>(
            Pair("settings", currentSettings),
            Pair("dateLastModified", now),
            Pair("expiresAt", gameExpiry(now)),
        )
        if (game.isPublic) updates += Pair("publicSince", now)
        gameDocRef.update(*updates.toTypedArray())
    }

    override suspend fun makeHost(gameCode: String, playerName: String) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val playerDocRef = gameDocRef.collection("players").document(playerName)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            val playerSnapshot = get(playerDocRef)
            val hostUid = if (playerSnapshot.exists) {
                playerSnapshot.data(Player.serializer()).ownerUid
            } else {
                ""
            }
            val now = Timestamp.now()
            val updates = mutableListOf<Pair<String, Any?>>(
                Pair("host", playerName),
                Pair("hostUid", hostUid),
                Pair("dateLastModified", now),
                Pair("expiresAt", gameExpiry(now)),
            )
            if (game.isPublic) updates += Pair("publicSince", now)
            update(gameDocRef, *updates.toTypedArray())
        }
    }

    override suspend fun removePlayer(gameCode: String, playerName: String) {
        val callerUid = FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val playerDocRef = gameDocRef.collection("players").document(playerName)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            val playersByName = game.orderedPlayerNames.mapNotNull { name ->
                val snapshot = get(gameDocRef.collection("players").document(name))
                if (snapshot.exists) {
                    name to snapshot.data(Player.serializer())
                } else {
                    null
                }
            }.toMap()
            val domainGame = mapToDomainGame(game, playersByName.values.toList()) ?: return@runTransaction
            val removalDecision = BankoGameEngine.removePlayer(domainGame, playerName)
            val removedUid = playersByName[playerName]?.ownerUid.orEmpty()
            val remainingPlayers = removalDecision.orderedPlayerNames.mapNotNull { playersByName[it] }
            val remainingHasRemovedUid = removedUid.isNotBlank() &&
                remainingPlayers.any { it.ownerUid == removedUid }
            val now = Timestamp.now()
            val updates = mutableMapOf<String, Any?>(
                "orderedPlayerNames" to removalDecision.orderedPlayerNames,
                "currentPlayer" to removalDecision.currentPlayer,
                "dateLastModified" to now,
                "expiresAt" to gameExpiry(now),
                "participantUids" to if (removedUid.isNotBlank() && !remainingHasRemovedUid) {
                    game.participantUids.filterNot { it == removedUid }
                } else {
                    game.participantUids
                },
            )
            if (game.isPublic) updates["publicSince"] = now
            if (game.host != playerName && game.hostUid != callerUid && removedUid == callerUid) {
                updates["lastLeftPlayer"] = playerName
            }
            removalDecision.round?.let { updates["round"] = it }
            if (removalDecision.shouldFinishGame) {
                // Drive the remaining player into the same game-over screen a normal end shows.
                updates["endRoundNum"] = -1
            }
            if (game.host == playerName) {
                val nextHost = remainingPlayers.firstOrNull { !it.hostCreated && !it.isBot }
                    ?: remainingPlayers.firstOrNull()
                updates["host"] = nextHost?.name.orEmpty()
                updates["hostUid"] = nextHost?.ownerUid.orEmpty()
            }
            update(gameDocRef, *updates.entries.map { it.key to it.value }.toTypedArray())
            delete(playerDocRef)
        }
    }

    override suspend fun bankPoints(
        gameCode: String,
        bankingPlayers: List<String>,
        nextRoundModifier: RoundModifier,
    ) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val firebaseGame = gameSnapshot.data(FirebaseGame.serializer())
            val players = firebaseGame.orderedPlayerNames.mapNotNull { name ->
                val playerSnapshot = get(gameDocRef.collection("players").document(name))
                if (playerSnapshot.exists) playerSnapshot.data(Player.serializer()) else null
            }
            val domainGame = mapToDomainGame(firebaseGame, players) ?: return@runTransaction
            val round = domainGame.round ?: return@runTransaction
            val bankDecision = BankoGameEngine.bank(
                game = domainGame,
                currentPlayer = domainGame.currentPlayer,
                bankingPlayerNames = bankingPlayers,
            ) ?: return@runTransaction
            val activeGinkoEvent = round.ginkoEvent
            val skimByRider = if (activeGinkoEvent == GinkoEvent.HEIST_WINDOW) {
                bankDecision.remainingPlayers.associateWith { riderName ->
                    val riderSnapshot = get(gameDocRef.collection("players").document(riderName))
                    if (riderSnapshot.exists) {
                        minOf(GinkoEvent.HEIST_SKIM_POINTS, riderSnapshot.data(Player.serializer()).points)
                    } else {
                        0
                    }
                }
            } else {
                emptyMap()
            }
            val heistBonuses = heistBonusesFor(bankDecision.bankingPlayers, skimByRider.values.sum())
            val updatedRecapStats = firebaseGame.recapStats.afterBank(
                round = round,
                bankingPlayerNames = bankDecision.bankingPlayers,
                heistBonuses = heistBonuses,
                heistSkims = skimByRider,
                roundEnded = bankDecision.remainingPlayers.isEmpty(),
            )
            bankDecision.bankingPlayers.forEach { bankingPlayer ->
                val bankingPlayerDocRef = gameDocRef.collection("players").document(bankingPlayer)
                val heistBonus = heistBonuses[bankingPlayer] ?: 0
                update(
                    bankingPlayerDocRef,
                    Pair("points", FieldValue.increment(round.currentPoints + heistBonus))
                )
            }
            skimByRider.forEach { (riderName, skim) ->
                if (skim > 0) {
                    val riderDocRef = gameDocRef.collection("players").document(riderName)
                    update(riderDocRef, Pair("points", FieldValue.increment(-skim)))
                }
            }
            val now = Timestamp.now()
            val gameUpdates = if (bankDecision.remainingPlayers.isEmpty()) {
                listOf(
                    Pair("round.roundNum", FieldValue.increment(1)),
                    Pair("round.currentPoints", nextRoundModifier.startingPoints()),
                    Pair("round.dieOne", 0),
                    Pair("round.dieTwo", 0),
                    Pair("round.currentRoll", 1),
                    Pair("round.activeOrderedPlayerNames", domainGame.orderedPlayerNames),
                    Pair("round.modifier", nextRoundModifier.name),
                    Pair("round.ginkoEvent", GinkoEvent.NONE.name),
                    Pair("round.ginkoEventsTriggered", 0),
                    Pair("round.bankLockoutTurnsRemaining", 0),
                    Pair("currentPlayer", bankDecision.nextPlayerName),
                    Pair("recapStats", updatedRecapStats),
                    Pair("dateLastModified", now),
                    Pair("expiresAt", gameExpiry(now)),
                )
            } else {
                listOf(
                    Pair("round.activeOrderedPlayerNames", bankDecision.remainingPlayers),
                    Pair("round.ginkoEvent", activeGinkoEvent.afterBank().name),
                    Pair("currentPlayer", bankDecision.nextPlayerName),
                    Pair("recapStats", updatedRecapStats),
                    Pair("dateLastModified", now),
                    Pair("expiresAt", gameExpiry(now)),
                )
            }
            update(gameDocRef, *gameUpdates.toTypedArray())
        }
    }

    override suspend fun rollDice(
        gameCode: String,
        expectedRound: Round?,
        round: Round,
        nextPlayerName: String,
        rolledValue: Int,
    ) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            if (game.round != expectedRound || game.currentPlayer != round.rolledBy) return@runTransaction
            val previousRound = game.round ?: return@runTransaction
            val now = Timestamp.now()
            update(
                gameDocRef,
                Pair("round", round),
                Pair("currentPlayer", nextPlayerName),
                Pair(
                    "recapStats",
                    game.recapStats.afterRoll(
                        previousRound = previousRound,
                        updatedRound = round,
                        rollerName = round.rolledBy,
                        rolledValue = rolledValue,
                    ),
                ),
                Pair("dateLastModified", now),
                Pair("expiresAt", gameExpiry(now)),
            )
        }
    }

    override suspend fun startGame(
        gameCode: String,
        activeOrderedPlayerNames: List<String>,
        firstRoundModifier: RoundModifier,
    ) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val now = Timestamp.now()
        gameDocRef.update(
            Pair(
                "round",
                Round(
                    roundNum = 1,
                    currentPoints = firstRoundModifier.startingPoints(),
                    dieOne = 0,
                    dieTwo = 0,
                    currentRoll = 1,
                    activeOrderedPlayerNames = activeOrderedPlayerNames,
                    modifier = firstRoundModifier,
                )
            ),
            Pair("isPublic", false),
            Pair("publicSince", null),
            Pair("recapStats", GameRecapStats()),
            Pair("dateLastModified", now),
            Pair("expiresAt", gameExpiry(now)),
        )
    }

    override suspend fun createGame(gameCode: String, name: String, hostPerksActive: Boolean, diceSkin: String, diceHat: String): String {
        val uid = FirebaseAuthSession.requireUid()
        val now = Timestamp.now()
        val expiresAt = gameExpiry(now)
        val player = Player(
            name = name,
            points = 0,
            hostCreated = true,
            expiresAt = expiresAt,
            diceSkin = diceSkin,
            diceHat = diceHat,
            ownerUid = uid,
        )
        val game = FirebaseGame(
            dateLastModified = now,
            expiresAt = expiresAt,
            joinCode = gameCode,
            endRoundNum = 10,
            orderedPlayerNames = listOf(name),
            host = name,
            hostUid = uid,
            currentPlayer = name,
            settings = GameSettings.defaultSettings,
            hostPerksActive = hostPerksActive,
            participantUids = listOf(uid),
            protocolVersion = GAME_PROTOCOL_VERSION,
        )
        val gameDocRef = db.collection("games").document(gameCode)
        val playerDocRef = gameDocRef.collection("players").document(name)
        db.runTransaction {
            if (get(gameDocRef).exists) {
                error("Game code collision.")
            }
            set(gameDocRef, game)
            set(playerDocRef, player)
        }
        return gameCode
    }

    override suspend fun rematch(gameCode: String, hostPerksActive: Boolean) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val playerIds = gameDocRef.collection("players").get().documents.map { it.id }
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            val now = Timestamp.now()
            playerIds.forEach { playerId ->
                update(gameDocRef.collection("players").document(playerId), Pair("points", 0))
            }
            update(
                gameDocRef,
                Pair("round", null),
                Pair("finished", false),
                Pair("currentPlayer", game.host),
                Pair("nudge", null),
                Pair("recapStats", GameRecapStats()),
                // A finishing player-removal forces game-over by dropping endRoundNum below 1;
                // restore the default so the rematch lobby isn't instantly over.
                Pair("endRoundNum", if (game.endRoundNum < 1) 10 else game.endRoundNum),
                Pair("hostPerksActive", hostPerksActive),
                Pair("dateLastModified", now),
                Pair("expiresAt", gameExpiry(now)),
            )
        }
    }

    override suspend fun joinGame(player: Player, gameCode: String): JoinGameResult {
        val uid = FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        val playerDocRef = gameDocRef.collection("players").document(player.name)
        val now = Timestamp.now()
        val expiresAt = gameExpiry(now)

        return db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction JoinGameResult.NOT_FOUND

            val game = gameSnapshot.data(FirebaseGame.serializer())
            if (!isGameProtocolSupported(game.protocolVersion)) {
                return@runTransaction JoinGameResult.VERSION_TOO_OLD
            }
            if (game.finished || game.round != null) {
                return@runTransaction JoinGameResult.ALREADY_STARTED
            }
            if (get(playerDocRef).exists ||
                game.orderedPlayerNames.any { it.equals(player.name, ignoreCase = true) }
            ) {
                return@runTransaction JoinGameResult.NAME_TAKEN
            }
            if (game.isPublic && game.orderedPlayerNames.size >= PUBLIC_GAME_MAX_PLAYERS) {
                return@runTransaction JoinGameResult.FULL
            }
            val updates = mutableListOf<Pair<String, Any?>>(
                Pair("orderedPlayerNames", game.orderedPlayerNames + player.name),
                Pair("participantUids", (game.participantUids + uid).distinct()),
                Pair("dateLastModified", now),
                Pair("expiresAt", expiresAt),
            )
            if (game.isPublic) updates += Pair("publicSince", now)
            update(gameDocRef, *updates.toTypedArray())
            set(playerDocRef, player.copy(expiresAt = expiresAt, ownerUid = uid))
            JoinGameResult.JOINED
        }
    }

    override suspend fun setGamePublic(gameCode: String, isPublic: Boolean) {
        FirebaseAuthSession.requireUid()
        val now = Timestamp.now()
        db.collection("games").document(gameCode).update(
            Pair("isPublic", isPublic),
            Pair("publicSince", if (isPublic) now else null),
            Pair("dateLastModified", now),
            Pair("expiresAt", gameExpiry(now)),
        )
    }

    override fun publicGamesFlow(): Flow<List<PublicGameSummary>> = flow {
        FirebaseAuthSession.requireUid()
        emitAll(
            combine(
                db.collection("games")
                    .where { "isPublic" equalTo true }
                    .limit(PUBLIC_GAME_QUERY_LIMIT)
                    .snapshots(),
                publicGameClockFlow(),
            ) { snapshot, now ->
                snapshot.documents.mapNotNull { document ->
                    val game = document.data(FirebaseGame.serializer())
                    if (!isDiscoverablePublicGame(game, now)) return@mapNotNull null
                    PublicGameSummary(
                        joinCode = game.joinCode,
                        hostName = game.host,
                        playerCount = game.orderedPlayerNames.size,
                        roundCount = game.endRoundNum,
                        ginkoEnabled = GameSettings.isEnabled(game.settings, GameSettings.GinkoMode),
                        hostPerksActive = game.hostPerksActive,
                        publishedAtSeconds = game.publicSince?.seconds ?: 0L,
                    )
                }.sortedByDescending { it.publishedAtSeconds }
            }
        )
    }

    override fun firebaseGameFlow(gameCode: String): Flow<FirebaseGame?> = flow {
        FirebaseAuthSession.requireUid()
        emitAll(
            db.collection("games").document(gameCode).snapshots().map { snapshot ->
                if (snapshot.exists) {
                    snapshot.data(FirebaseGame.serializer())
                } else {
                    null
                }
            }
        )
    }

    override fun firebasePlayersFlow(gameCode: String): Flow<List<Player>> = flow {
        FirebaseAuthSession.requireUid()
        emitAll(
            db.collection("games").document(gameCode).collection("players").snapshots().map { snapshot ->
                snapshot.documents.map { it.data(Player.serializer()) }
            }
        )
    }

    override fun tableReactionsFlow(gameCode: String): Flow<List<TableReaction>> = flow {
        FirebaseAuthSession.requireUid()
        emitAll(
            db.collection("games").document(gameCode).collection("reactions").snapshots().map { snapshot ->
                snapshot.documents.map { it.data(TableReaction.serializer()) }
            }
        )
    }

    override fun domainGameFlow(gameCode: String): Flow<DomainGame?> =
        combine(firebaseGameFlow(gameCode), firebasePlayersFlow(gameCode)) { firebaseGame, players ->
            mapToDomainGame(firebaseGame, players)
        }

    override suspend fun gameExists(gameCode: String): Boolean {
        FirebaseAuthSession.requireUid()
        val docRef = db.collection("games").document(gameCode)
        val snapshot = docRef.get()
        return snapshot.exists
    }

    override suspend fun updatePlayerOrder(gameCode: String, playerNames: List<String>) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            val now = Timestamp.now()
            val updates = mutableListOf<Pair<String, Any?>>(
                Pair("orderedPlayerNames", playerNames),
                Pair("dateLastModified", now),
                Pair("expiresAt", gameExpiry(now)),
            )
            if (game.isPublic) updates += Pair("publicSince", now)
            update(gameDocRef, *updates.toTypedArray())
        }
    }

    override suspend fun updateEndRoundNum(gameCode: String, endRoundNum: Int) {
        FirebaseAuthSession.requireUid()
        val gameDocRef = db.collection("games").document(gameCode)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            val now = Timestamp.now()
            val updates = mutableListOf<Pair<String, Any?>>(
                Pair("endRoundNum", endRoundNum),
                Pair("dateLastModified", now),
                Pair("expiresAt", gameExpiry(now)),
            )
            if (game.isPublic) updates += Pair("publicSince", now)
            update(gameDocRef, *updates.toTypedArray())
        }
    }

    override suspend fun addPlayer(player: Player, gameCode: String) {
        val uid = FirebaseAuthSession.requireUid()
        val validation = PlayerNameValidator.validate(player.name)
        if (validation !is PlayerNameValidator.Result.Valid) return
        val validatedPlayer = player.copy(name = validation.name, ownerUid = uid)
        val gameDocRef = db.collection("games").document(gameCode)
        val playerDocRef = gameDocRef.collection("players").document(validatedPlayer.name)
        db.runTransaction {
            val gameSnapshot = get(gameDocRef)
            if (!gameSnapshot.exists) return@runTransaction
            val game = gameSnapshot.data(FirebaseGame.serializer())
            if (game.orderedPlayerNames.any { it.equals(validatedPlayer.name, ignoreCase = true) }) {
                return@runTransaction
            }
            val now = Timestamp.now()
            val expiresAt = gameExpiry(now)
            val updates = mutableListOf<Pair<String, Any?>>(
                Pair("orderedPlayerNames", game.orderedPlayerNames + validatedPlayer.name),
                Pair("participantUids", (game.participantUids + uid).distinct()),
                Pair("dateLastModified", now),
                Pair("expiresAt", expiresAt),
            )
            if (game.isPublic) updates += Pair("publicSince", now)
            update(gameDocRef, *updates.toTypedArray())
            set(playerDocRef, validatedPlayer.copy(expiresAt = expiresAt))
        }
    }

    override suspend fun sendNudge(gameCode: String, nudge: Nudge) {
        FirebaseAuthSession.requireUid()
        val now = Timestamp.now()
        db.collection("games").document(gameCode).update(
            Pair("nudge", nudge),
            Pair("dateLastModified", now),
            Pair("expiresAt", gameExpiry(now)),
        )
    }

    override suspend fun sendTableReaction(gameCode: String, reaction: TableReaction) {
        val uid = FirebaseAuthSession.requireUid()
        val now = Timestamp.now()
        // One document per auth uid makes this an event stream with bounded storage: each sender's
        // next reaction replaces their last one, while different players can still react together.
        db.collection("games")
            .document(gameCode)
            .collection("reactions")
            .document(uid)
            .set(
                FirebaseTableReactionWrite(
                    eventId = reaction.eventId,
                    senderPlayer = reaction.senderPlayer,
                    targetPlayer = reaction.targetPlayer,
                    kind = reaction.kind,
                    roundNum = reaction.roundNum,
                    currentRoll = reaction.currentRoll,
                    ownerUid = uid,
                    // Firestore resolves this sentinel to request.time. Rules can therefore enforce
                    // a real server-side cooldown without trusting the device clock.
                    createdAt = FieldValue.serverTimestamp,
                    expiresAt = gameExpiry(now),
                )
            )
    }

    private companion object {
        // 6h after the last activity. Single-session party games finish well within this, so an
        // active game is never reaped; abandoned games (closed mid-play, lobbies nobody joined) are.
        const val GAME_TTL_SECONDS = 6L * 60 * 60
        const val PUBLIC_GAME_QUERY_LIMIT = 40
    }
}

/** Write shape for serverTimestamp; reads use the public [TableReaction] Timestamp model. */
@Serializable
private data class FirebaseTableReactionWrite(
    val eventId: String,
    val senderPlayer: String,
    val targetPlayer: String,
    val kind: TableReactionKind,
    val roundNum: Int,
    val currentRoll: Int,
    val ownerUid: String,
    val createdAt: FieldValue,
    val expiresAt: Timestamp,
)

private fun publicGameClockFlow(): Flow<Timestamp> = flow {
    while (true) {
        emit(Timestamp.now())
        delay(PUBLIC_GAME_CLOCK_REFRESH_MS)
    }
}

internal fun isDiscoverablePublicGame(game: FirebaseGame, now: Timestamp = Timestamp.now()): Boolean {
    if (!game.isPublic || game.finished || game.round != null) return false
    if (game.orderedPlayerNames.size >= PUBLIC_GAME_MAX_PLAYERS) return false
    return isPublicListingFresh(game.publicSince?.seconds, now.seconds)
}

private const val PUBLIC_GAME_CLOCK_REFRESH_MS = 30_000L

private fun mapToDomainGame(firebaseGame: FirebaseGame?, players: List<Player>): DomainGame? {
    if (firebaseGame == null) {
        return null
    }
    return DomainGame(
        dateLastModified = firebaseGame.dateLastModified.toString(),
        joinCode = firebaseGame.joinCode,
        players = players,
        round = firebaseGame.round,
        endRoundNum = firebaseGame.endRoundNum,
        orderedPlayerNames = firebaseGame.orderedPlayerNames,
        host = firebaseGame.host,
        hostUid = firebaseGame.hostUid,
        currentPlayer = firebaseGame.currentPlayer,
        settings = firebaseGame.settings,
        finished = firebaseGame.finished,
        nudge = firebaseGame.nudge,
        hostPerksActive = firebaseGame.hostPerksActive,
        isPublic = firebaseGame.isPublic,
        publicSinceSeconds = firebaseGame.publicSince?.seconds,
        participantUids = firebaseGame.participantUids,
        recapStats = firebaseGame.recapStats,
    )
}
